import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { useAuth } from "@/contexts/AuthContext";
import { usePeople } from "@/lib/hooks/usePeople";
import { ownerMarkKey, teachingStaff } from "../people/staff";
import { call, CALLABLES } from "@/services/callables";
import { teacherRemoval } from "./rules";
import type { Group } from "@/types";

/**
 * Who teaches the group.
 *
 * Any number of them; one hosts each lesson. **The last one cannot be
 * removed**, and the reason is on screen rather than in a failed request: a
 * group with no teacher cannot run a lesson at all.
 */
export function TeachersTab({ group }: { group: Group }) {
  const t = useT();
  const { success, showError } = useToast();
  const { user } = useAuth();
  const { people } = usePeople();
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  // The owner is on this list too: they teach, and they host lessons.
  const staff = teachingStaff(people);
  const mark = (person: (typeof staff)[number]) => {
    const key = ownerMarkKey(person, user?.uid);
    return key ? <span className="ml-2 text-xs text-ink-muted">{t(key)}</span> : null;
  };
  const theirs = staff.filter((person) => group.teacherUids.includes(person.uid));
  const candidates = staff.filter((person) => !group.teacherUids.includes(person.uid));
  const isLastTeacher = group.teacherUids.length <= 1;

  const remove = async (uid: string) => {
    setBusy(true);
    try {
      await call(CALLABLES.removeGroupTeacher, { groupId: group.id, uid });
      success("common.saved");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex justify-end">
        <Button onClick={() => setAdding(true)}>{t("group.teachers.add")}</Button>
      </div>

      <ul className="mt-4 divide-y divide-line">
        {theirs.map((person) => (
          <li key={person.uid} className="flex items-center justify-between gap-4 py-3">
            <Link
              to={`/people/${person.uid}`}
              className="text-sm text-ink underline-offset-2 hover:underline"
            >
              {person.displayName}
            </Link>
            {mark(person)}
            <span className="flex-1" />
            <Button
              variant="ghost"
              busy={busy}
              disabled={teacherRemoval(group, person.uid) !== "ok"}
              onClick={() => remove(person.uid)}
            >
              {t("group.teachers.remove")}
            </Button>
          </li>
        ))}
      </ul>

      {isLastTeacher ? (
        <p className="mt-4 rounded-card border border-warning bg-warning-soft px-4 py-3 text-sm text-ink-soft">
          {t("group.teachers.lastOne")}
        </p>
      ) : null}

      <Modal open={adding} onClose={() => setAdding(false)} titleKey="group.teachers.chooseTeacher">
        {candidates.length === 0 ? (
          <p className="text-sm text-ink-muted">{t("group.teachers.noneToAdd")}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {candidates.map((person) => (
              <li key={person.uid} className="flex items-center justify-between gap-4">
                <span className="text-sm text-ink">
                  {person.displayName}
                  {mark(person)}
                </span>
                <Button
                  variant="secondary"
                  busy={busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await call(CALLABLES.addGroupTeacher, {
                        groupId: group.id,
                        uid: person.uid,
                      });
                      success("common.saved");
                      setAdding(false);
                    } catch (error) {
                      showError(error);
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  {t("common.add")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </div>
  );
}
